import axios from 'axios';
import { useCallback, useEffect, useMemo, useReducer } from 'react';

const initialState = {
  notableDates: [],
  isFetching: false,
  error: '',
};

function notableDatesReducer(state, action) {
  switch (action.type) {
    case 'fetch-start':
      return { ...state, isFetching: true, error: '' };
    case 'fetch-success':
      return {
        ...state,
        isFetching: false,
        notableDates: action.notableDates || [],
        error: '',
      };
    case 'fetch-error':
      return { ...state, isFetching: false, error: action.error || '' };
    case 'upsert': {
      const saved = action.notableDate;
      if (!saved?.id) return state;
      const withoutSaved = state.notableDates.filter((date) => date.id !== saved.id);
      return {
        ...state,
        notableDates: [saved, ...withoutSaved].sort((a, b) =>
          String(b.event_date || '').localeCompare(String(a.event_date || ''))
        ),
      };
    }
    case 'remove':
      return {
        ...state,
        notableDates: state.notableDates.filter((date) => date.id !== action.id),
      };
    default:
      return state;
  }
}

function normalizeTicker(ticker) {
  return String(ticker || '').trim().toUpperCase();
}

function isMissingRefreshTokenError(error) {
  const message = String(
    error?.message || error?.error_description || error?.error || ''
  ).toLowerCase();

  return (
    error?.error === 'missing_refresh_token' ||
    message.includes('missing refresh token') ||
    message.includes('missing_refresh_token')
  );
}

export function getNotableDateKeyForFiling(filing) {
  const accession = String(filing?.id || filing?.accession_number || '').trim();
  if (accession) {
    return `accession:${accession}`;
  }
  return [
    'filing',
    filing?.filingDate || '',
    filing?.href || '',
    filing?.formType || '',
  ].join(':');
}

export function getNotableDateKey(notableDate) {
  const accession = String(notableDate?.accession_number || '').trim();
  if (accession) {
    return `accession:${accession}`;
  }
  return [
    'filing',
    notableDate?.event_date || '',
    notableDate?.filing_url || '',
    notableDate?.form_type || '',
  ].join(':');
}

export function buildNotableDatePayloadFromFiling(ticker, filing) {
  const eventDescription =
    filing?.ai?.summary140 ||
    filing?.description ||
    filing?.formType ||
    'SEC filing';

  return {
    ticker: normalizeTicker(ticker),
    event_date: filing?.filingDate,
    event_description: String(eventDescription).slice(0, 240),
    filing_url: filing?.href || null,
    accession_number: filing?.id || null,
    form_type: filing?.formType || null,
  };
}

export function useNotableDates({
  apiBaseUrl,
  ticker,
  getAccessTokenSilently,
  isAuthenticated,
}) {
  const [state, dispatch] = useReducer(notableDatesReducer, initialState);
  const normalizedTicker = normalizeTicker(ticker);

  const notableDateByFilingKey = useMemo(() => {
    const map = new Map();
    for (const notableDate of state.notableDates) {
      map.set(getNotableDateKey(notableDate), notableDate);
    }
    return map;
  }, [state.notableDates]);

  const fetchNotableDates = useCallback(async (signal) => {
    if (!isAuthenticated || !normalizedTicker) {
      dispatch({ type: 'fetch-success', notableDates: [] });
      return [];
    }

    dispatch({ type: 'fetch-start' });
    try {
      const token = await getAccessTokenSilently({ detailedResponse: false });
      const endpoint = `${apiBaseUrl}/api/notable-dates?ticker=${encodeURIComponent(normalizedTicker)}`;
      const { data } = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      const notableDates = data?.notable_dates || [];
      dispatch({ type: 'fetch-success', notableDates });
      return notableDates;
    } catch (err) {
      if (axios.isCancel(err) || err?.name === 'CanceledError') {
        return [];
      }
      if (isMissingRefreshTokenError(err)) {
        dispatch({ type: 'fetch-success', notableDates: [] });
        return [];
      }
      const message = err?.response?.data?.error || err?.message || 'Unable to load notable dates.';
      dispatch({ type: 'fetch-error', error: message });
      return [];
    }
  }, [apiBaseUrl, getAccessTokenSilently, isAuthenticated, normalizedTicker]);

  useEffect(() => {
    const controller = new AbortController();
    fetchNotableDates(controller.signal);
    return () => controller.abort();
  }, [fetchNotableDates]);

  const saveFilingDate = useCallback(async (filing) => {
    if (!isAuthenticated) {
      return 'auth-required';
    }
    if (!filing?.filingDate) {
      throw new Error('This filing does not have a filing date to save.');
    }

    const token = await getAccessTokenSilently({ detailedResponse: false });
    const payload = buildNotableDatePayloadFromFiling(normalizedTicker, filing);
    const { data } = await axios.post(
      `${apiBaseUrl}/api/notable-dates`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const notableDate = data?.notable_date;
    if (notableDate) {
      dispatch({ type: 'upsert', notableDate });
    }
    return notableDate;
  }, [apiBaseUrl, getAccessTokenSilently, isAuthenticated, normalizedTicker]);

  const deleteNotableDate = useCallback(async (notableDate) => {
    if (!isAuthenticated) {
      return 'auth-required';
    }
    if (!notableDate?.id) {
      return false;
    }

    const token = await getAccessTokenSilently({ detailedResponse: false });
    await axios.delete(
      `${apiBaseUrl}/api/notable-dates/${encodeURIComponent(notableDate.id)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    dispatch({ type: 'remove', id: notableDate.id });
    return true;
  }, [apiBaseUrl, getAccessTokenSilently, isAuthenticated]);

  return {
    ...state,
    deleteNotableDate,
    notableDateByFilingKey,
    refreshNotableDates: fetchNotableDates,
    saveFilingDate,
  };
}
